import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { faUpload } from '@fortawesome/pro-light-svg-icons/faUpload';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { SMLRouteNames } from '../../../../routing/sml/SMLRoutes';
import { OutreachShell } from '../components/OutreachShell';
import { OutreachPlanCard } from '../components/OutreachPlanCard';
import { useContacts, ContactFilters } from '../hooks/useContacts';
import { useOutreachPlan } from '../hooks/useOutreachPlan';
import { outreachMessages } from '../outreachMessages';
import { ContactsSidebar } from './ContactsSidebar';
import { ContactsFilterBar } from './ContactsFilterBar';
import { useOutreachLists } from '../hooks/useOutreachLists';
import { ContactsToolbar } from './ContactsToolbar';
import { ContactsTable } from './ContactsTable';
import { ContactsBulkBar } from './ContactsBulkBar';
import { ContactsEmpty } from './ContactsEmpty';
import { ContactDrawer } from './ContactDrawer';
import { ImportDialog } from './ImportDialog';
import { AddContactModal } from './AddContactModal';
import { ContactsMoreMenu } from './ContactsMoreMenu';
import { contactsMessages as messages } from './contactsMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Contacts.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ContactsScreenProps {
  accountId: number;
  initialFilters: ContactFilters;
}

/**
 * Contacts (design outreach-v2 k1): everyone Plug pitched and the artist's own imports, with the
 * address. No hidden email and no reveal. The filter chips, the lists, the import, the export (Pro
 * and up) and the bulk actions. A row opens the drawer (outreach q2).
 */
const ContactsScreenComponent: React.FC<ContactsScreenProps> = ({ accountId, initialFilters }) => {
  const intl = useIntl();
  const plan = useOutreachPlan();
  const [filters, setFilters] = useState<ContactFilters>(initialFilters);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [openUuid, setOpenUuid] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const contacts = useContacts(accountId, filters);
  const { lists } = useOutreachLists(accountId);

  const changeFilters = useCallback((patch: Partial<ContactFilters>) => {
    setFilters((current) => ({ ...current, ...patch }));
    setSelected(new Set());
  }, []);
  const toggle = useCallback((uuid: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }
      return next;
    });
  }, []);
  const picked = useMemo(
    () => contacts.rows.filter((row) => selected.has(row.workspaceContactUuid)),
    [contacts.rows, selected]
  );
  const hasFilters =
    filters.listUuid !== null ||
    filters.status !== null ||
    filters.tag !== null ||
    filters.q.trim() !== '' ||
    (filters.source ?? null) !== null ||
    (filters.kind ?? null) !== null;
  const openImport = useCallback(() => plan.guard(SMLUpgradeFeature.outreach, () => setIsImporting(true)), [plan]);
  const openAdd = useCallback(() => plan.guard(SMLUpgradeFeature.outreach, () => setIsAdding(true)), [plan]);

  const content = () => {
    if (contacts.isLoading) {
      return <LoadingView />;
    }
    if (contacts.error !== null) {
      return <ErrorView error={contacts.error ?? undefined} retry={contacts.retry} />;
    }
    if (contacts.rows.length === 0) {
      return (
        <ContactsEmpty
          hasFilters={hasFilters}
          onClear={() => changeFilters({ listUuid: null, status: null, tag: null, q: '', source: null, kind: null })}
          onImport={openImport}
          onAdd={openAdd}
        />
      );
    }
    return (
      <>
        <ContactsTable
          accountId={accountId}
          rows={contacts.rows}
          selected={selected}
          onToggle={toggle}
          onOpen={setOpenUuid}
        />
        {contacts.hasMore && (
          <div>
            <SMLButton
              outline
              omitCap
              isLoading={contacts.isLoadingMore}
              onClick={() => contacts.loadMore().catch(console.error)}
            >
              {intl.formatMessage(outreachMessages.showMore)}
            </SMLButton>
          </div>
        )}
      </>
    );
  };

  return (
    <OutreachShell
      section="tabs"
      tab={SMLRouteNames.outreachContacts}
      lead={intl.formatMessage(messages.intro)}
      actions={
        <>
          <SMLButton size="sm" outline omitCap leftIcon={faUpload} onClick={openImport} disabled={plan.isDemo}>
            {intl.formatMessage(messages.importCsv)}
          </SMLButton>
          <ContactsMoreMenu accountId={accountId} listUuid={filters.listUuid} onAddContact={openAdd} />
        </>
      }
    >
      {!plan.canPitch && <OutreachPlanCard />}
      <ContactsFilterBar filters={filters} counts={contacts.counts} onChange={changeFilters} />
      <div className={lists.length > 0 ? styles.layoutLists : styles.layout}>
        <ContactsSidebar accountId={accountId} filters={filters} onChange={changeFilters} />
        <div className={shared.stack}>
          <ContactsToolbar filters={filters} onChange={changeFilters} total={contacts.total} />
          {content()}
        </div>
      </div>
      {picked.length > 0 && (
        <ContactsBulkBar accountId={accountId} rows={picked} onDone={() => setSelected(new Set())} />
      )}
      <ContactDrawer accountId={accountId} workspaceContactUuid={openUuid} onClose={() => setOpenUuid(null)} />
      {isImporting && <ImportDialog accountId={accountId} onClose={() => setIsImporting(false)} />}
      {isAdding && <AddContactModal accountId={accountId} onClose={() => setIsAdding(false)} />}
    </OutreachShell>
  );
};

export const ContactsScreen = TypedMemo(ContactsScreenComponent);
